import { TOrgTheme, setDevicePref } from '@eventr/common'
import {
    ColoursObj,
    Gutters,
    MenuButton,
    StyledPullToRefresh,
    TColourSchemes,
    Typography,
} from '@eventr/common-storybook'
import { Radio, Stack } from '@mui/material'
import { deviceDetect } from 'react-device-detect'
import i18n from '../../../i18n/i18next'
import { bs } from '../../../state/bound.store'
import { useThemeContext } from '../../../state/themeState/themeContext'
import { getDevicePrefName } from '../../../utils'
import {
    setStatusBarStyleDark,
    setStatusBarStyleLight,
} from '../../appState/statusBar'
import { NavigationView } from '../../layout/NavigationView'
export default function ThemesPage() {
    const { setScheme, setOverride, getOverride, setCustomId, setThemeImages } =
        useThemeContext()

    function handleThemeChange(themesName: TColourSchemes, object?: any) {
        if (object) {
            setThemeImages(object.images)
            setCustomId(object._id)
            setDevicePref(
                'themePalette',
                object,
                getDevicePrefName(deviceDetect)
            )
        } else {
            setDevicePref('theme', themesName, getDevicePrefName(deviceDetect))
            setThemeImages({})
        }

        themesName === 'light' || themesName === 'custom'
            ? setStatusBarStyleLight()
            : setStatusBarStyleDark()

        setScheme(themesName, object)
        setOverride(true)
    }

    const selectedColourScheme = useThemeContext((s) => s.selectedColourScheme)
    const selectedId = useThemeContext((s) => s.customThemeId)
    const defaultTheme = useThemeContext((s) => s.defaultTheme)

    return (
        <NavigationView
            navigationBarTitle={i18n.t('account.themes')}
            backButtonAction={() => {
                window.pushPath('/account/settings')
            }}
            usePageTransition
        >
            <StyledPullToRefresh
                onRefresh={async () => {
                    /// Nothing
                }}
                promiseFunction={async () => {
                    await bs().fetchOrgConfig()
                    await bs().getProfile()
                }}
                maxPullDownDistance={70}
            >
                <Gutters>
                    <Stack gap={'16px'}>
                        <MenuButton
                            id={'eventrTheme'}
                            key={1}
                            onClick={() => {
                                const preferredColorScheme = window.matchMedia(
                                    '(prefers-color-scheme: dark)'
                                ).matches
                                    ? 'dark'
                                    : 'light'
                                setDevicePref(
                                    'theme',
                                    '',
                                    getDevicePrefName(deviceDetect)
                                )
                                setScheme(preferredColorScheme)
                                setOverride(false)
                                setCustomId('')
                            }}
                            style={{
                                background: ColoursObj.backgrounds.secondary,
                            }}
                        >
                            <Stack
                                flexDirection="row"
                                gap="16px"
                                alignItems="center"
                            >
                                <Radio
                                    style={{ padding: '0px' }}
                                    sx={{
                                        color: ColoursObj.font.primary,
                                    }}
                                    checked={!getOverride()}
                                />
                                <Stack alignItems={'flex-start'}>
                                    <Typography
                                        variant="bodyRegular"
                                        color={ColoursObj.font.primary}
                                    >
                                        {i18n.t(
                                            defaultTheme
                                                ? 'themes.default'
                                                : 'themes.device'
                                        )}
                                    </Typography>
                                    {defaultTheme &&
                                        defaultTheme?.themeName && (
                                            <Typography
                                                variant="bodySubtext"
                                                color={
                                                    ColoursObj.font.secondary
                                                }
                                            >
                                                {defaultTheme.themeName}
                                            </Typography>
                                        )}
                                </Stack>
                            </Stack>
                        </MenuButton>
                        <MenuButton
                            id={'eventrTheme'}
                            key={'light'}
                            onClick={() => {
                                handleThemeChange('light')
                            }}
                            style={{
                                background: ColoursObj.backgrounds.secondary,
                            }}
                        >
                            <Stack
                                flexDirection="row"
                                gap="16px"
                                alignItems="center"
                            >
                                <Radio
                                    style={{ padding: '0px' }}
                                    sx={{
                                        color: ColoursObj.font.primary,
                                    }}
                                    checked={
                                        getOverride() &&
                                        selectedColourScheme === 'light'
                                    }
                                />
                                <Typography
                                    variant="bodyRegular"
                                    color={ColoursObj.font.primary}
                                >
                                    {i18n.t('themes.light')}
                                </Typography>
                            </Stack>
                        </MenuButton>
                        <MenuButton
                            id={'eventrTheme'}
                            key={'dark'}
                            onClick={() => {
                                handleThemeChange('dark')
                            }}
                            style={{
                                background: ColoursObj.backgrounds.secondary,
                            }}
                        >
                            <Stack
                                flexDirection="row"
                                gap="16px"
                                alignItems="center"
                            >
                                <Radio
                                    style={{ padding: '0px' }}
                                    sx={{
                                        color: ColoursObj.font.primary,
                                    }}
                                    checked={
                                        getOverride() &&
                                        selectedColourScheme === 'dark'
                                    }
                                />
                                <Typography
                                    variant="bodyRegular"
                                    color={ColoursObj.font.primary}
                                >
                                    {i18n.t('themes.dark')}
                                </Typography>
                            </Stack>
                        </MenuButton>
                        {(bs().orgConfig?.themes || []).map(
                            (object: TOrgTheme) => {
                                return (
                                    <MenuButton
                                        id={'eventrTheme'}
                                        key={object.themeName}
                                        onClick={() => {
                                            handleThemeChange('custom', object)
                                        }}
                                        style={{
                                            background:
                                                ColoursObj.backgrounds
                                                    .secondary,
                                        }}
                                    >
                                        <Stack
                                            flexDirection="row"
                                            gap="16px"
                                            alignItems="center"
                                        >
                                            <Radio
                                                style={{ padding: '0px' }}
                                                sx={{
                                                    color: ColoursObj.font
                                                        .primary,
                                                }}
                                                checked={
                                                    selectedColourScheme ===
                                                        'custom' &&
                                                    selectedId === object._id
                                                }
                                            />
                                            <Typography
                                                variant="bodyRegular"
                                                color={ColoursObj.font.primary}
                                            >
                                                {object.themeName}
                                            </Typography>
                                        </Stack>
                                    </MenuButton>
                                )
                            }
                        )}
                    </Stack>
                </Gutters>
            </StyledPullToRefresh>
        </NavigationView>
    )
}
